import { createSelector } from "reselect";

import Role from "@mapmycustomers/shared/enum/Role";
import User from "@mapmycustomers/shared/types/User";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";

import RoleFilter from "@app/scene/reports/components/PermissionsReport/enum/RoleFilter";
import buildPermissionsReportRows from "@app/scene/reports/components/PermissionsReport/utils/buildPermissionsReportRows";
import isUserInTeamScope from "@app/scene/reports/components/PermissionsReport/utils/isUserInTeamScope";
import { doesOrganizationSupportMileageTracking, isCurrentUserOwner } from "@app/store/iam";
import { getTeams, getTeamsOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

const ROLE_FILTER_KEYS: Record<RoleFilter, Role[]> = {
  [RoleFilter.ALL]: [],
  [RoleFilter.MANAGERS]: [Role.MANAGER],
  [RoleFilter.MEMBERS]: [Role.MEMBER, Role.READ_ONLY, Role.READ_ONLY_WITH_EXPORTS],
  [RoleFilter.OWNERS]: [Role.OWNER],
};

const matchesSearchQuery = (user: User, searchQuery: string) => {
  const query = searchQuery.trim().toLowerCase();
  if (!query) {
    return true;
  }

  return (
    userDisplayName(user).toLowerCase().includes(query) ||
    user.username.toLowerCase().includes(query)
  );
};

export const getPermissionsReportState = (state: RootState) => state.scene.report.permissionsReport;

export const getPermissionsReportRoleFilter = createSelector(
  getPermissionsReportState,
  ({ roleFilter }) => roleFilter
);

export const getPermissionsReportSearchQuery = createSelector(
  getPermissionsReportState,
  ({ searchQuery }) => searchQuery
);

export const getPermissionsReportTeamId = createSelector(
  getPermissionsReportState,
  ({ teamId }) => teamId
);

export const getPermissionsReportUsers = createSelector(
  getPermissionsReportState,
  ({ users }) => users
);

export const hasPermissionsReportFailed = createSelector(
  getPermissionsReportState,
  ({ failed }) => failed
);

export const isPermissionsReportLoading = createSelector(
  getPermissionsReportState,
  ({ loading }) => loading
);

// Owners see every team; everyone else only the teams they can access, as on the other reports.
export const getPermissionsReportTeams = createSelector(
  isCurrentUserOwner,
  getTeamsOfEntireOrganization,
  getTeams,
  (isOwner, organizationTeams, accessibleTeams) => (isOwner ? organizationTeams : accessibleTeams)
);

const getScopeTeamIds = createSelector(
  isCurrentUserOwner,
  getPermissionsReportTeams,
  (isOwner, teams) => (isOwner ? undefined : new Set(teams.map(({ id }) => id)))
);

// In-scope reps before any filter; by team membership, since viaTeamAccess also admits owners.
const getActiveUsers = createSelector(
  getPermissionsReportUsers,
  getScopeTeamIds,
  (users, scopeTeamIds) =>
    users.filter((user) => !user.deactivated && isUserInTeamScope(user, scopeTeamIds))
);

export const hasAnyPermissionsReportData = createSelector(getActiveUsers, (users) =>
  users.some(({ devicePermissions }) => !!devicePermissions?.length)
);

// Search, role and team narrow the rep list; the table sorts and pages the device columns.
const getFilteredUsers = createSelector(
  getActiveUsers,
  getPermissionsReportRoleFilter,
  getPermissionsReportSearchQuery,
  getPermissionsReportTeamId,
  (users, roleFilter, searchQuery, teamId) => {
    const roleKeys = ROLE_FILTER_KEYS[roleFilter];

    return users.filter(
      (user) =>
        (!roleKeys.length || roleKeys.includes(user.role.key)) &&
        (!teamId || user.teams.some(({ id }) => id === teamId)) &&
        matchesSearchQuery(user, searchQuery)
    );
  }
);

export const getPermissionsReportRows = createSelector(
  getFilteredUsers,
  doesOrganizationSupportMileageTracking,
  buildPermissionsReportRows
);
